import api from './api'

/**
 * Get reviews pending for the current user
 * @param {Object} options - Query options
 * @param {number} options.limit - Max reviews to fetch
 * @param {string} options.cursor - Pagination cursor
 * @param {string} options.formId - Optional form filter
 * @returns {Promise<{reviews: Array, hasMore: boolean, nextCursor: string}>}
 */
export async function getMyReviews({ limit = 50, cursor, formId } = {}) {
  const params = new URLSearchParams()
  if (limit) params.append('limit', limit.toString())
  if (cursor) params.append('cursor', cursor)
  if (formId) params.append('formId', formId)

  const response = await api.get(`/my-reviews?${params.toString()}`)
  return response.data
}

/**
 * Approve the current stage of a submission
 * @param {string} submissionId - The submission ID
 * @param {string} comments - Optional approval comments
 * @param {{typedName: string, intentConfirmed: boolean}} [attestation] - Sign-off
 *   attestation (#607 Phase 2), required by the backend only when the active
 *   review step has `requireSignoffAttestation` set; omitted otherwise.
 * @returns {Promise<{message: string, workflowInstance: Object}>}
 */
export async function approveSubmission(submissionId, comments = null, attestation = null) {
  const payload = { comments }
  if (attestation) {
    payload.attestation = attestation
  }
  const response = await api.post(`/submissions/${submissionId}/workflow/approve`, payload)
  return response.data
}

/**
 * Return a submission for corrections
 * @param {string} submissionId - The submission ID
 * @param {string} comments - Comment explaining what needs fixing (required for non-collaborative)
 * @param {Array} sectionReturns - For collaborative submissions: [{sectionId: string, comment: string}]
 * @returns {Promise<{message: string, workflowInstance: Object}>}
 */
export async function returnSubmission(submissionId, comments, sectionReturns = null) {
  const payload = { comments }
  if (sectionReturns) {
    payload.sectionReturns = sectionReturns
  }
  const response = await api.post(`/submissions/${submissionId}/workflow/return`, payload)
  return response.data
}

/**
 * Return a UNIFIED-workflow submission (#135) for corrections.
 *
 * Matches the contract `handleUnifiedReturn` in `return-submission.ts` reads:
 * a single shared, required `comments` string plus an optional `sectionIds`
 * string[] (the preceding collaboration group's section ids to reopen; an
 * empty/omitted list reopens ALL). This is distinct from the legacy
 * `returnSubmission` per-section `sectionReturns` shape.
 *
 * @param {string} submissionId - The submission ID
 * @param {string} comment - Required comment explaining what needs fixing
 * @param {string[]} [sectionIds] - Section ids to reopen (omit/empty = all)
 * @returns {Promise<Object>}
 */
export async function returnUnifiedSubmission(submissionId, comment, sectionIds = null) {
  const payload = { comments: (comment || '').trim() }
  if (Array.isArray(sectionIds) && sectionIds.length > 0) {
    payload.sectionIds = sectionIds
  }
  const response = await api.post(`/submissions/${submissionId}/workflow/return`, payload)
  return response.data
}

/**
 * Get workflow state for a submission
 * @param {string} submissionId - The submission ID
 * @returns {Promise<{workflowInstance: Object}>}
 */
export async function getWorkflow(submissionId) {
  const response = await api.get(`/submissions/${submissionId}/workflow`)
  return response.data
}

/**
 * Get workflow history for a submission
 * @param {string} submissionId - The submission ID
 * @returns {Promise<{history: Array}>}
 */
export async function getWorkflowHistory(submissionId) {
  const response = await api.get(`/submissions/${submissionId}/workflow/history`)
  return response.data
}

/**
 * Edit submission data as a reviewer during workflow review
 * This is different from regular submission updates - it:
 * - Checks reviewer permissions (not form contributor permissions)
 * - Verifies the stage allows editing (canEdit flag)
 * - Records changes in workflow history
 * @param {string} submissionId - The submission ID
 * @param {Object} submissionData - The updated submission data
 * @param {string} comments - Optional comment explaining the changes
 * @returns {Promise<{message: string, changes: Object, workflowInstance: Object}>}
 */
export async function editSubmissionAsReviewer(submissionId, submissionData, comments = null) {
  const response = await api.post(`/submissions/${submissionId}/workflow/edit`, {
    submissionData,
    comments
  })
  return response.data
}

/**
 * Get submissions returned to the current user for corrections
 * @param {Object} options - Query options
 * @param {number} options.limit - Max items to fetch
 * @param {string} options.cursor - Pagination cursor
 * @returns {Promise<{returned: Array, hasMore: boolean, nextCursor: string}>}
 */
export async function getMyReturned({ limit = 50, cursor } = {}) {
  const params = new URLSearchParams()
  if (limit) params.append('limit', limit.toString())
  if (cursor) params.append('cursor', cursor)

  const response = await api.get(`/my-returned?${params.toString()}`)
  return response.data
}
